// Local-mode mocks for added users (managed accounts, POST /managedAccounts/*).
// Stateful in memory until reload. web-01.prod (1) has contractor-demo
// (password, active, 6 days left), vendor-x (key, pending; active after 2
// reads) and old-vendor (removed at its expiry). A create adds a pending row
// that goes active after 2 reads; a remove goes removing, then removed after
// 2 reads. "Reads" are /managedAccounts/list and /instances/localUsers calls —
// localUsersMocks.js shows these rows as its added users.
// The local adapter always answers 200, so error replies aren't simulated.

import { SSH_PROXY_MOCKS } from "./sshProxyMocks";

const MIN = 60e3;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
const ago = (ms) => new Date(Date.now() - ms).toISOString();
const ahead = (ms) => new Date(Date.now() + ms).toISOString();
const now = () => new Date().toISOString();

const HELPER_VERSION = 7;

const hostOf = (id) =>
  (SSH_PROXY_MOCKS["/instances/list"]?.instances || []).find((r) => r.instanceId === Number(id))?.hostName || "";

let seq = 10;
const uuid = () => {
  seq += 1;
  return `9d4f2b70-0000-4000-8000-${String(seq).padStart(12, "0")}`;
};

// Each row may carry a countdown (`_left` reads) to its next status.
const accounts = [
  {
    id: "9d4f2b70-0000-4000-8000-000000000001", endpointId: 1, hostname: "web-01.prod", account: "contractor-demo",
    credential: "password", intervalDays: 30, note: "CHG-1042 Acme migration", status: "active",
    expiresAt: ahead(6 * DAY), createdBy: 1, createdAt: ago(1 * DAY), activeAt: ago(1 * DAY - MIN),
  },
  {
    id: "9d4f2b70-0000-4000-8000-000000000002", endpointId: 1, hostname: "web-01.prod", account: "vendor-x",
    credential: "key", note: "Vendor X support window", status: "pending",
    expiresAt: ahead(30 * DAY), createdBy: 1, createdAt: ago(20 * 1000), _left: 2,
  },
  {
    id: "9d4f2b70-0000-4000-8000-000000000003", endpointId: 1, hostname: "web-01.prod", account: "old-vendor",
    credential: "key", note: "", status: "removed",
    expiresAt: ago(3 * DAY), createdBy: 1, createdAt: ago(10 * DAY), activeAt: ago(10 * DAY - MIN),
    endedAt: ago(3 * DAY), endReason: "expired", removedAt: ago(3 * DAY - MIN),
  },
];

const advance = (a) => {
  if (a._left === undefined) return;
  a._left -= 1;
  if (a._left > 0) return;
  delete a._left;
  if (a.status === "pending") Object.assign(a, { status: "active", activeAt: now() });
  else if (a.status === "removing") Object.assign(a, { status: "removed", removedAt: now() });
};

const view = (a) => {
  const out = { ...a };
  delete out._left;
  return out;
};

// For localUsersMocks.js: one endpoint's rows (0 = all), newest first,
// advanced one read.
export function readManagedAccounts(instanceId) {
  const id = Number(instanceId) || 0;
  const mine = accounts.filter((a) => !id || a.endpointId === id);
  mine.forEach(advance);
  return mine
    .slice()
    .sort((x, y) => new Date(y.createdAt) - new Date(x.createdAt))
    .map(view);
}

// For localUsersMocks.js: add a row as it is, once per endpoint and account.
export function seedManagedAccount(row) {
  if (accounts.some((a) => a.endpointId === row.endpointId && a.account === row.account)) return;
  accounts.push({ id: uuid(), hostname: hostOf(row.endpointId), createdBy: 1, createdAt: now(), note: "", ...row });
}

export const MANAGED_ACCOUNTS_MOCKS = [
  {
    pattern: "/managedaccounts/list",
    respond: (body) => ({
      managedAccounts: readManagedAccounts(body?.instanceId),
      helperVersion: HELPER_VERSION,
      minHours: 1,
      maxDays: 90,
    }),
  },
  {
    // { instanceId, account, credential: "key" | "password", intervalDays, expiresAt: "" | RFC 3339, note }
    pattern: "/managedaccounts/create",
    respond: (body) => {
      const b = body || {};
      const endpointId = Number(b.instanceId) || 0;
      const credential = b.credential === "password" ? "password" : "key";
      const a = {
        id: uuid(), endpointId, hostname: hostOf(endpointId), account: String(b.account || "").trim(), credential,
        ...(credential === "password" ? { intervalDays: Number(b.intervalDays) || 30 } : {}),
        note: String(b.note || "").trim().slice(0, 300), status: "pending",
        expiresAt: b.expiresAt ? new Date(b.expiresAt).toISOString() : null, createdBy: 1, createdAt: now(), _left: 2,
      };
      accounts.unshift(a);
      return { managedAccount: view(a) };
    },
  },
  {
    pattern: "/managedaccounts/remove",
    respond: (body) => {
      const a = accounts.find((x) => x.id === body?.id);
      if (a && (a.status === "pending" || a.status === "active")) {
        Object.assign(a, { status: "removing", endedAt: now(), endReason: "removed by an administrator", _left: 2 });
      }
      return { status: "removing" };
    },
  },
];
